/**
 * Feature Flags Hook
 * Provides feature flag access throughout the app
 * Citation: /frontend/src/hooks/useFeatureFlags.js
 */

import { useState, useEffect, useContext, createContext } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const FeatureFlagsContext = createContext(null);

export function FeatureFlagsProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false); // Changed to false - no need to wait for API
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    // Default features - enable everything for now until backend endpoint is implemented
    const defaultFeatures = {
      nudges_enabled: true,
      protocol_discovery: true,
      intelligent_analytics: true,
      struggle_detection: true,
      team_dashboards: true,
      chart_session_tracking: true,
      ai_protocol_suggest: true,
      protocol_compliance_scoring: true,
      protocol_versioning: true,
      api_access: true,
      white_label: true,
      custom_branding: true,
      extension_popup_search: true,
      context_menu_protocols: true,
      auto_protocol_injection: true,
      ui_insights: true
    };

    // TODO: Implement /features/my-features endpoint in backend
    // For now, just use default features immediately
    console.log('📋 Feature flags: Using default configuration (all enabled)');
    setFeatures(defaultFeatures);
    setLoading(false);
    
    /* Backend endpoint not yet implemented
    try {
      const token = localStorage.getItem('prism_token');
      if (!token) {
        console.warn('⚠️ No auth token found, using default features');
        setFeatures(defaultFeatures);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/features/my-features`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });

      setFeatures(response.data.features || defaultFeatures);
      setConfig(response.data.config || {});
      setError(null);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
      console.warn('⚠️ Feature flags API failed, using default features');
      setFeatures(defaultFeatures);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    */
  };

  const isEnabled = (featureName) => {
    return features[featureName] === true;
  };

  const getConfig = (key, defaultValue = null) => {
    return config[key] !== undefined ? config[key] : defaultValue;
  };

  const value = {
    features,
    config,
    loading,
    error,
    isEnabled,
    getConfig,
    reload: loadFeatures
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
}

// Feature names enum for IDE autocomplete
export const Features = {
  NUDGES: 'nudges_enabled',
  PROTOCOL_DISCOVERY: 'protocol_discovery',
  INTELLIGENT_ANALYTICS: 'intelligent_analytics',
  STRUGGLE_DETECTION: 'struggle_detection',
  TEAM_DASHBOARDS: 'team_dashboards',
  CHART_SESSION_TRACKING: 'chart_session_tracking',
  AI_PROTOCOL_SUGGEST: 'ai_protocol_suggest',
  PROTOCOL_COMPLIANCE: 'protocol_compliance_scoring',
  PROTOCOL_VERSIONING: 'protocol_versioning',
  API_ACCESS: 'api_access',
  WHITE_LABEL: 'white_label',
  CUSTOM_BRANDING: 'custom_branding',
  EXTENSION_POPUP_SEARCH: 'extension_popup_search',
  CONTEXT_MENU_PROTOCOLS: 'context_menu_protocols',
  AUTO_PROTOCOL_INJECTION: 'auto_protocol_injection',
  UI_INSIGHTS: 'ui_insights'
};
